import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import PatientAgentContextCard from './PatientAgentContextCard';

// The patient's clinical context in a sheet over whatever is on screen: from
// the chat (edit without leaving the conversation) and from a note ("Usar
// como contexto para Sissi", which opens the editor with the note appended).

type OpenOptions = { appendText?: string };

type ContextSheetValue = {
  openContextSheet: (patient: { id: number; name: string }, options?: OpenOptions) => void;
};

const ContextSheetContext = createContext<ContextSheetValue | undefined>(undefined);

export const PatientContextSheetProvider = ({ children }: { children: ReactNode }) => {
  const [target, setTarget] = useState<{ patient: { id: number; name: string }; appendText?: string } | null>(null);
  const [open, setOpen] = useState(false);

  const openContextSheet = useCallback((patient: { id: number; name: string }, options: OpenOptions = {}) => {
    setTarget({ patient, appendText: options.appendText });
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openContextSheet }), [openContextSheet]);

  return (
    <ContextSheetContext.Provider value={value}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full gap-0 sm:max-w-lg">
          <SheetHeader className="border-b border-border">
            <SheetTitle>{target?.patient.name ?? 'Contexto clínico'}</SheetTitle>
            {/* The card below carries the visible heading. */}
            <SheetDescription className="sr-only">Contexto clínico para Sissi AI</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {target && (
              <PatientAgentContextCard
                key={`${target.patient.id}-${target.appendText ?? ''}`}
                patient={target.patient}
                appendText={target.appendText}
                className="border-0 p-0"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </ContextSheetContext.Provider>
  );
};

export const usePatientContextSheet = (): ContextSheetValue | null => useContext(ContextSheetContext) ?? null;
